import {
  ANGER_TYPE_HEALING,
  ANGER_TYPE_SURVIVAL,
  getHealthyAngerLabel
} from "../constants/angerPatterns.js";
import { getInsightCombinedText, getInsightEmotion } from "./mentalPatternAnalysis.js";

const ANGER_EMOTIONS = new Set([
  "Angry",
  "Frustrated",
  "Hostile",
  "Rage",
  "Furious",
  "Irritated",
  "Annoyed",
  "Critical"
]);

const ANGER_TEXT_SIGNALS =
  /\b(angry|anger|rage|raging|furious|mad at|lash(?:ed)? out|lashed out|blow(?:ing)? up|bitter(?:ness)?|hostile|resentment|resentful|seeing red|heated)\b/i;

/** Recognition / boundary anger (healing) */
const HEALING_SIGNALS = [
  /\b(shrink(?:ing)? myself|make myself small|made myself small|smaller around)\b/,
  /\b(giving in to control|gave in to control|under (?:their|his|her) control|controlled by)\b/,
  /\b(tolerat(?:e|ing|ed) (?:things|what) that hurt|put up with (?:hurt|harm|abuse)|accepted (?:hurt|harm))\b/,
  /\b(betray(?:ing|ed)? myself|abandon(?:ing|ed)? myself|left myself|lost myself)\b/,
  /\b(internaliz(?:e|ing|ed) (?:blame|guilt|shame)|blame myself|guilty for|ashamed of myself)\b/,
  /\b(recogniz(?:e|ing|ed)|finally see|starting to see|wake up to|breaking (?:these|this) pattern|break the pattern)\b/,
  /\b(boundar(?:y|ies)|stand up for myself|speak up for myself|no longer (?:accept|tolerate)|won't tolerate)\b/,
  /\b(deserve better|treated unfairly|unfair treatment|disrespected|not okay anymore)\b/,
  /\b(people[- ]?pleas.*angry|angry.*people[- ]?pleas)\b/
];

/** Control / harm anger (survival) */
const SURVIVAL_SIGNALS = [
  /\b(threat(?:en|ening|ened)?|threats)\b/,
  /\b(blame (?:them|him|her|you)|blaming (?:them|others)|point(?:ing)? fingers)\b/,
  /\b(dominat(?:e|ing|ion)|dominant|dominance)\b/,
  /\b(entitle(?:d|ment)|entitled)\b/,
  /\b(power over|establish(?:ing)? power|control (?:them|others|him|her))\b/,
  /\b(make (?:them|him|her|others) feel small|need (?:them|others) to feel small)\b/,
  /\b(yell(?:ed|ing)? at (?:them|him|her)|scream(?:ed|ing)? at|punish(?:ed|ing)?)\b/,
  /\b(get even|revenge|payback|make them pay)\b/,
  /\b(they need to learn|put them in their place|show them who's boss)\b/
];

const RECENCY_DAYS = 14;
const RECENCY_BOOST = 3;
const MID_DAYS = 30;
const MID_BOOST = 1.5;

function parseInsightDate(insight) {
  const raw = insight.created_at != null ? insight.created_at : insight.createdAt;
  const d = raw ? new Date(raw) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function recencyWeight(date, now) {
  const ageDays = (now - date.getTime()) / 86400000;
  if (ageDays <= RECENCY_DAYS) return RECENCY_BOOST;
  if (ageDays <= MID_DAYS) return MID_BOOST;
  return 1;
}

export function isAngerInsight(insight) {
  const emotion = getInsightEmotion(insight);
  if (ANGER_EMOTIONS.has(emotion)) return true;
  return ANGER_TEXT_SIGNALS.test(getInsightCombinedText(insight));
}

export function scoreAngerSignals(text) {
  let healing = 0;
  let survival = 0;
  for (const re of HEALING_SIGNALS) {
    if (re.test(text)) healing += 2;
  }
  for (const re of SURVIVAL_SIGNALS) {
    if (re.test(text)) survival += 2;
  }
  return { healing, survival };
}

export function classifyAngerType(insight) {
  const text = getInsightCombinedText(insight);
  const { healing, survival } = scoreAngerSignals(text);

  if (healing > survival) return ANGER_TYPE_HEALING;
  if (survival > healing) return ANGER_TYPE_SURVIVAL;

  if (healing === 0 && survival === 0) {
    const emotion = getInsightEmotion(insight);
    if (["Guilty", "Ashamed", "Insecure", "Sad", "Hurt"].includes(emotion)) return ANGER_TYPE_HEALING;
    if (emotion === "Hostile") return ANGER_TYPE_SURVIVAL;
    if (emotion === "Frustrated" || emotion === "Irritated" || emotion === "Annoyed") return ANGER_TYPE_SURVIVAL;
    return ANGER_TYPE_HEALING;
  }

  return healing >= survival ? ANGER_TYPE_HEALING : ANGER_TYPE_SURVIVAL;
}

export function analyzeAngerPatterns(insights) {
  const list = Array.isArray(insights) ? insights : [];
  const now = Date.now();

  const angerInsights = [];
  let healingCount = 0;
  let survivalCount = 0;
  let weightedHealing = 0;
  let weightedSurvival = 0;

  for (const item of list) {
    if (!isAngerInsight(item)) continue;

    const date = parseInsightDate(item);
    const type = classifyAngerType(item);
    const signals = scoreAngerSignals(getInsightCombinedText(item));
    const weight = recencyWeight(date, now);

    angerInsights.push({
      id: item.id,
      date: date.toISOString(),
      type,
      emotion: getInsightEmotion(item),
      preview: (item.entry_text || "").slice(0, 120),
      healingScore: signals.healing,
      survivalScore: signals.survival
    });

    if (type === ANGER_TYPE_HEALING) healingCount += 1;
    else survivalCount += 1;

    weightedHealing += (signals.healing + (type === ANGER_TYPE_HEALING ? 2 : 0)) * weight;
    weightedSurvival += (signals.survival + (type === ANGER_TYPE_SURVIVAL ? 2 : 0)) * weight;
  }

  angerInsights.sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalAnger = angerInsights.length;
  const totalWeight = weightedHealing + weightedSurvival;
  const healthyAngerScore =
    totalWeight > 0 ? Math.round((weightedHealing / totalWeight) * 100) : totalAnger > 0 ? 50 : null;
  const healthyAngerLabel = healthyAngerScore != null ? getHealthyAngerLabel(healthyAngerScore) : null;

  return {
    angerInsights,
    totalAnger,
    healingCount,
    survivalCount,
    healthyAngerScore,
    healthyAngerLabel
  };
}
